import { html, render } from 'lit';
import { directive, Directive } from 'lit/directive.js';
import { appendStyle } from '../append-style';
export class AnimationMap extends Directive {
    constructor(partInfo) {
        super(partInfo);
        this.view = document.createElement('div');
        this.lock = false;
        appendStyle(this.view, {
            display: 'flex',
            gap: '8px',
            position: 'relative',
        });
    }
    render(items, controller) {
        this.applyDirection(controller.direction);
        if (this.lock) {
            return html `${this.view}`;
        }
        if (!this.view.children.length) {
            this.addedElementsWithoutAnimation(items, controller);
            return html `${this.view}`;
        }
        this.transition(items, controller).catch(console.error);
        return html `${this.view}`;
    }
    async transition(items, controller) {
        const [keyMap, indexMap] = this.getKeyMapAndIndexMap(items, controller);
        const viewIndexMap = this.getIndexMapFromView();
        const renderCandidate = this.getAllRenderCandidate(viewIndexMap, indexMap);
        const removeCandidate = this.getAllRemoveCandidate(viewIndexMap, indexMap);
        const moveCandidate = this.getAllMoveCandidate(viewIndexMap, indexMap);
        const renderCandidateElements = this.getAllRenderCandidateElements(renderCandidate, keyMap, controller);
        const removeCandidateElements = this.getAllCandidateElements(removeCandidate);
        const moveCandidateElements = this.getAllCandidateElements(moveCandidate);
        const renderCandidateNodeIndexMap = new Map();
        const removeCandidateNodeIndexMap = new Map();
        const moveCandidateNodeIndexMap = new Map();
        renderCandidateElements.forEach((element, key) => {
            renderCandidateNodeIndexMap.set(indexMap.get(key), element);
        });
        removeCandidateElements.forEach((element, key) => {
            removeCandidateNodeIndexMap.set(viewIndexMap.get(key), element);
        });
        moveCandidateElements.forEach((element, key) => {
            moveCandidateNodeIndexMap.set([viewIndexMap.get(key), indexMap.get(key)], element);
        });
        const renderCandidateNodeEntries = [...renderCandidateElements.entries()];
        const removeCandidateNodeEntries = [...removeCandidateElements.entries()];
        const moveCandidateNodeEntries = [...moveCandidateElements.entries()];
        await controller.onAnimationStart?.();
        const [, renderResultState, removeResultState, moveResultState] = await Promise.all([
            controller.onBeforeAnimation?.(this.view, renderCandidateNodeIndexMap, removeCandidateNodeIndexMap, moveCandidateNodeIndexMap),
            this.executeHandlerAndReturnResultMap(renderCandidateNodeEntries, (node, key) => controller.onBeforeRenderAnimateItem(node, indexMap.get(key))),
            this.executeHandlerAndReturnResultMap(removeCandidateNodeEntries, (node, key) => controller.onBeforeRemoveAnimateItem(node, viewIndexMap.get(key))),
            this.executeHandlerAndReturnResultMap(moveCandidateNodeEntries, (node, key) => controller.onBeforeMoveAnimationItem?.(node, viewIndexMap.get(key), indexMap.get(key)) ?? Promise.resolve()),
        ]);
        this.renderNode(renderCandidateElements, indexMap);
        this.removeNode(removeCandidateElements);
        this.moveNode(moveCandidateElements, viewIndexMap, indexMap);
        await Promise.all([
            this.executeHandlerAndReturnResultMap(renderCandidateNodeEntries, (node, key) => controller.onAfterRenderAnimateItem?.(node, indexMap.get(key), renderResultState.get(key)) ?? Promise.resolve()),
            this.executeHandlerAndReturnResultMap(removeCandidateNodeEntries, (node, key) => controller.onAfterRemoveAnimateItem?.(node, viewIndexMap.get(key), removeResultState.get(key)) ?? Promise.resolve()),
            this.executeHandlerAndReturnResultMap(moveCandidateNodeEntries, (node, key) => controller.onAfterMoveAnimationItem?.(node, moveResultState.get(key)) ?? Promise.resolve()),
            controller.onAfterAnimation?.(this.view, renderCandidateNodeIndexMap, removeCandidateNodeIndexMap, moveCandidateNodeIndexMap),
        ]);
        await controller.onAnimationComplete?.();
    }
    async executeHandlerAndReturnResultMap(nodes, handler) {
        const results = new Map();
        await Promise.all(nodes.map(async ([key, node]) => {
            const result = await handler(node, key);
            results.set(key, result);
        }));
        return results;
    }
    getAllRenderCandidate(indexMap, newIndexMap) {
        const candidates = new Set();
        newIndexMap.forEach((_, key) => {
            if (!indexMap.has(key)) {
                candidates.add(key);
            }
        });
        return candidates;
    }
    getAllRemoveCandidate(indexMap, newIndexMap) {
        const candidates = new Set();
        indexMap.forEach((_, key) => {
            if (!newIndexMap.has(key)) {
                candidates.add(key);
            }
        });
        return candidates;
    }
    getAllMoveCandidate(indexMap, newIndexMap) {
        const candidates = new Set();
        const keys = new Set([...indexMap.keys(), ...newIndexMap.keys()]);
        keys.forEach((key) => {
            const oldIndex = indexMap.get(key);
            const newIndex = newIndexMap.get(key);
            if (oldIndex === undefined || newIndex === undefined)
                return;
            if (oldIndex === newIndex)
                return;
            candidates.add(key);
        });
        return candidates;
    }
    getAllRenderCandidateElements(candidateSet, keyMap, controller) {
        const candidateMap = new Map();
        let index = 0;
        candidateSet.forEach((key) => {
            const value = keyMap.get(key);
            if (!value)
                return;
            const element = this.createItem(key);
            const template = controller.onTemplateBuilder(value, index);
            render(template, element);
            candidateMap.set(key, element);
            index++;
        });
        return candidateMap;
    }
    getAllCandidateElements(candidateSet) {
        const candidateMap = new Map();
        candidateSet.forEach((key) => {
            const element = this.view.querySelector(`#${key}`);
            if (!element)
                return;
            candidateMap.set(key, element);
        });
        return candidateMap;
    }
    renderNode(candidateElements, indexMap) {
        candidateElements.forEach((node, key) => {
            const index = indexMap.get(key) ?? 0;
            const referenceNode = this.view.children[index];
            if (referenceNode) {
                this.view.insertBefore(node, referenceNode);
            }
            else {
                this.view.appendChild(node);
            }
        });
    }
    removeNode(nodeMap) {
        nodeMap.forEach((node) => {
            node.remove();
        });
    }
    moveNode(candidateElements, oldIndexMap, newIndexMap) {
        const viewIndexMap = this.getIndexMapFromView();
        candidateElements.forEach((node, key) => {
            const oldIndex = oldIndexMap.get(key);
            const newIndex = newIndexMap.get(key);
            const currentIndex = viewIndexMap.get(key);
            if (currentIndex === newIndex) {
                return;
            }
            let referenceNode;
            if (oldIndex > newIndex) {
                referenceNode = this.view.children[newIndex];
            }
            if (oldIndex < newIndex) {
                referenceNode = this.view.children[oldIndex];
            }
            if (!referenceNode)
                return;
            this.view.insertBefore(node, referenceNode);
        });
    }
    getKeyMapAndIndexMap(items, controller) {
        const keyMap = new Map();
        const indexMap = new Map();
        let index = 0;
        for (const item of items) {
            const key = controller.onKeyExtractor(item, index);
            keyMap.set(key, item);
            indexMap.set(key, index);
            index++;
        }
        return [keyMap, indexMap];
    }
    getIndexMapFromView() {
        const indexMap = new Map();
        let index = 0;
        this.view.childNodes.forEach((node) => {
            indexMap.set(node.id, index++);
        });
        return indexMap;
    }
    addedElementsWithoutAnimation(items, controller) {
        let index = 0;
        for (const item of items) {
            const key = controller.onKeyExtractor(item, index);
            const element = this.createItem(key);
            const template = controller.onTemplateBuilder(item, index);
            render(template, element);
            this.view.appendChild(element);
            index++;
        }
    }
    createItem(key) {
        const element = document.createElement('div');
        element.id = key.toString();
        return element;
    }
    applyDirection(direction) {
        let flexDirection;
        if (direction === 'vertical') {
            flexDirection = 'column';
        }
        if (direction === 'horizontal') {
            flexDirection = 'row';
        }
        appendStyle(this.view, {
            flexDirection,
        });
    }
}
export const animationMap = directive(AnimationMap);
//# sourceMappingURL=animation-map.js.map